import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, OnDestroy, OnInit, DoCheck } from '@angular/core';

import { DroppableDirective } from './ngx-droppable.directive';
import { DrakeStoreService } from '../services/drake-store.service';

/**
 * Adds properties and events to draggable elements
 */
@Directive({ selector: '[ngxDraggable]' })
export class DraggableDirective implements OnInit, OnDestroy, DoCheck {
  @Input() disabled: boolean;
  @Input() ngxDraggable: string[];
  @Input() model: any;

  @Input()
  get dropZones(): any {
    return this._dropZones || this.ngxDraggable || this._parentDropzones;
  }
  set dropZones(val: any) {
    this._dropZones = val;
  }

  // tslint:disable-next-line:no-input-rename
  @Input('moves') _moves = true;

  /*
  ContentChildren doesn't get children created with NgTemplateOutlet
  See https://github.com/angular/angular/issues/14842
  Implemented via updateElements method
  @ContentChildren(DragHandleDirective, {descendants: true})
  handlesList: QueryList<DragHandleDirective>; */

  handles: any[] = [];

  get hasHandle() {
    return !!this.handles.length;
  }

  @Output()
  drag: EventEmitter<any> = new EventEmitter<any>();

  dragDelay = 200; // milliseconds
  dragDelayed = true;

  touchTimeout: any;

  get element(): any {
    return this.el.nativeElement;
  }

  _dropZones: string[];
  _parentDropzones: string[];

  constructor(private el: ElementRef, private drakesService: DrakeStoreService, private droppableDirective: DroppableDirective) {}

  // From: https://github.com/bevacqua/dragula/issues/289#issuecomment-277143172
  @HostListener('touchmove', ['$event'])
  onMove(e: Event) {
    if (!this._moves || this.dragDelayed) {
      e.stopPropagation();
      clearTimeout(this.touchTimeout);
    }
  }

  @HostListener('touchstart', ['$event'])
  onDown(e: Event) {
    if (this._moves) {
      this.touchTimeout = setTimeout(() => {
        this.dragDelayed = false;
      }, this.dragDelay);
    }
  }

  @HostListener('touchend', ['$event'])
  onUp(e: Event) {
    if (this._moves) {
      clearTimeout(this.touchTimeout as number);
      this.dragDelayed = true;
    }
  }

  ngOnInit() {
    this.update();
  }

  update() {
    this._parentDropzones = [this.droppableDirective.dropZone];
    this.drakesService.registerDraggable(this);
    this.updateElements();
  }

  ngOnDestroy() {
    this.drakesService.removeDraggable(this);
  }

  updateElements() {
    const nativeElement = this.el.nativeElement;
    const handles: NodeList = nativeElement.querySelectorAll('[ngxdraghandle]');
    this.handles = Array.from(handles).filter((h: any) => findFirstDraggableParent(h) === nativeElement);

    function findFirstDraggableParent(c: any) {
      while (c.parentNode) {
        c = c.parentNode;
        if (c.hasAttribute && c.hasAttribute('ngxdraggable')) {
          return c;
        }
      }
    }
  }

  canMove(source?: any, handle?: any, sibling?: any): boolean {
    if (typeof this._moves === 'boolean') {
      return this._moves;
    }
    if (typeof this._moves === 'function') {
      // tslint:disable-next-line:ban-types
      return (this._moves as Function)(this.model, source, handle, sibling);
    }
    return true;
  }

  moves(source: any, handle: any, sibling: any): boolean {
    if (!this.canMove(source, handle, sibling)) {
      return false;
    }

    return this.hasHandle ? this.handles.some(h => handelFor(handle, h)) : true;

    function handelFor(c: any, p: any) {
      if (c === p) {
        return true;
      }
      // tslint:disable-next-line:no-conditional-assignment, curly
      while ((c = c.parentNode) && c !== p);
      return !!c;
    }
  }

  ngDoCheck() {
    this.updateElements();
  }
}
